import { modApi } from "../api";
import type { GhostBan, OnlineGhost, Role, SanctionKind } from "../types";
import { confirmAction } from "../ui/ConfirmDialog";

/**
 * Acoes de moderacao comuns ao painel do jogo e ao painel administrativo.
 * Cada uma abre o modal de confirmacao e chama a API; o servidor confere de
 * novo o cargo e os limites.
 */

export interface ModTarget {
  id: string;
  /** Nome publico (o que aparece na cidade). */
  name: string;
  username: string;
}

export type ModActionId =
  | "warn"
  | "mute"
  | "unmute"
  | "kick"
  | "suspend"
  | "unsuspend"
  | "ban"
  | "unban"
  | "resetName"
  | "resetAvatar";

export const MOD_ACTION_LABELS: Record<ModActionId, string> = {
  warn: "Avisar",
  mute: "Silenciar",
  unmute: "Remover silêncio",
  kick: "Expulsar",
  suspend: "Suspender",
  unsuspend: "Remover suspensão",
  ban: "Banir",
  unban: "Remover banimento",
  resetName: "Resetar nome",
  resetAvatar: "Resetar avatar",
};

/** Rotulos das acoes no registro de auditoria (action do servidor). */
export const AUDIT_LABELS: Record<string, string> = {
  mute: "silenciou",
  unmute: "removeu o silêncio de",
  suspend: "suspendeu",
  unsuspend: "removeu a suspensão de",
  ban: "baniu",
  unban: "removeu o banimento de",
  kick: "expulsou",
  ghost_kick: "expulsou o fantasma",
  ghost_ban: "baniu (pelo IP) o fantasma",
  ghost_unban: "removeu o banimento do fantasma",
  warn: "avisou",
  reset_name: "resetou o nome de",
  reset_avatar: "resetou o avatar de",
  update_account: "editou o cadastro de",
  set_password: "trocou a senha de",
  end_sessions: "encerrou as sessões de",
  delete_scores: "apagou pontuações de",
  delete_account: "excluiu a conta",
  announce: "anunciou",
  report_resolved: "resolveu uma denúncia contra",
  report_dismissed: "descartou uma denúncia contra",
  filter_add: "adicionou ao filtro o termo",
  filter_remove: "removeu do filtro o termo",
};

const MODERATOR_DURATIONS: [number, string][] = [
  [10, "10 minutos"],
  [30, "30 minutos"],
  [60, "1 hora"],
  [360, "6 horas"],
  [1440, "1 dia"],
  [4320, "3 dias"],
  [10080, "7 dias"],
];

const ADMIN_DURATIONS: [number, string][] = [
  ...MODERATOR_DURATIONS,
  [43200, "30 dias"],
  [129600, "90 dias"],
  [525600, "1 ano"],
];

function durationField(role: Role, preset: number) {
  const list = role === "admin" ? ADMIN_DURATIONS : MODERATOR_DURATIONS;
  return {
    name: "minutes",
    label: "Duração",
    type: "select" as const,
    value: String(preset),
    options: list.map(([value, label]) => ({ value: String(value), label })),
  };
}

const reasonField = (required = false) => ({
  name: "reason",
  label: required ? "Motivo" : "Motivo (opcional, o jogador vê)",
  type: "textarea" as const,
  maxLength: 200,
  required,
});

/** Quais acoes o cargo pode usar (o painel esconde o resto). */
export function actionsFor(role: Role): ModActionId[] {
  const base: ModActionId[] = ["warn", "mute", "unmute", "kick", "suspend", "unsuspend", "resetName", "resetAvatar"];
  return role === "admin" ? [...base, "ban", "unban"] : base;
}

const who = (t: ModTarget) => (t.name === t.username ? t.name : `${t.name} (@${t.username})`);

/** Abre o modal da acao. Resolve com a mensagem de sucesso, ou null se cancelou. */
export function runModAction(action: ModActionId, target: ModTarget, token: string, role: Role): Promise<string | null> {
  const name = who(target);
  const sanction = (kind: SanctionKind) => (v: Record<string, string>) =>
    modApi.sanction(token, target.id, kind, Number(v.minutes ?? 0), v.reason ?? "").then(() => undefined);
  const revoke = (kind: SanctionKind, done: string) => () => modApi.revoke(token, target.id, kind).then(() => done);

  switch (action) {
    case "warn":
      return confirmAction({
        title: "Enviar aviso",
        message: `${name} vai ver este aviso em destaque na tela.`,
        confirmLabel: "Enviar aviso",
        fields: [{ name: "message", label: "Aviso", type: "textarea", maxLength: 200, required: true }],
        run: (v) => modApi.warn(token, target.id, v.message).then((r) => r.message),
      });
    case "mute":
      return confirmAction({
        title: "Silenciar jogador",
        message: `${name} continua na cidade, mas não consegue mandar mensagens no chat.`,
        confirmLabel: "Silenciar",
        fields: [durationField(role, 30), reasonField()],
        run: (v) => sanction("mute")(v).then(() => `${target.name} foi silenciado.`),
      });
    case "unmute":
      return confirmAction({
        title: "Remover silêncio",
        message: `${name} volta a poder conversar no chat.`,
        confirmLabel: "Remover silêncio",
        run: revoke("mute", `${target.name} pode conversar de novo.`),
      });
    case "kick":
      return confirmAction({
        title: "Expulsar jogador",
        message: `${name} sai da cidade agora, mas pode entrar de novo quando quiser.`,
        confirmLabel: "Expulsar",
        danger: true,
        fields: [reasonField()],
        run: (v) => modApi.kick(token, target.id, v.reason).then((r) => r.message),
      });
    case "suspend":
      return confirmAction({
        title: "Suspender conta",
        message: `${name} sai da cidade agora e não consegue entrar até a suspensão acabar.`,
        confirmLabel: "Suspender",
        danger: true,
        fields: [durationField(role, 1440), reasonField(true)],
        run: (v) => sanction("suspend")(v).then(() => `${target.name} foi suspenso.`),
      });
    case "unsuspend":
      return confirmAction({
        title: "Remover suspensão",
        message: `${name} volta a poder entrar na cidade.`,
        confirmLabel: "Remover suspensão",
        run: revoke("suspend", `A suspensão de ${target.name} foi removida.`),
      });
    case "ban":
      return confirmAction({
        title: "Banir conta",
        message: `${name} sai da cidade agora e não entra mais, até um admin remover o banimento.`,
        confirmLabel: "Banir permanentemente",
        danger: true,
        fields: [reasonField(true)],
        run: (v) => sanction("ban")(v).then(() => `${target.name} foi banido.`),
      });
    case "unban":
      return confirmAction({
        title: "Remover banimento",
        message: `${name} volta a poder entrar na cidade.`,
        confirmLabel: "Remover banimento",
        run: revoke("ban", `O banimento de ${target.name} foi removido.`),
      });
    case "resetName":
      return confirmAction({
        title: "Resetar nome público",
        message: `O nome público de ${name} volta a ser o nome de usuário (${target.username}). A pessoa reconecta automaticamente.`,
        confirmLabel: "Resetar nome",
        fields: [reasonField()],
        run: (v) => modApi.resetName(token, target.id, v.reason).then((r) => r.message),
      });
    case "resetAvatar":
      return confirmAction({
        title: "Resetar avatar",
        message: `O avatar de ${name} volta ao padrão e o editor abre para a pessoa montar outro.`,
        confirmLabel: "Resetar avatar",
        fields: [reasonField()],
        run: (v) => modApi.resetAvatar(token, target.id, v.reason).then((r) => r.message),
      });
  }
}

/**
 * Acoes sobre um fantasma (visitante sem conta): expulsar, ou banir o IP dele
 * da entrada como fantasma. Moderadores tambem podem banir.
 */
export function runGhostAction(action: "kick" | "ban", ghost: OnlineGhost, token: string): Promise<string | null> {
  if (action === "kick") {
    return confirmAction({
      title: "Expulsar fantasma",
      message: `${ghost.name} sai da cidade agora, mas pode entrar de novo quando quiser.`,
      confirmLabel: "Expulsar",
      danger: true,
      fields: [reasonField()],
      run: (v) => modApi.kickGhost(token, ghost.id, v.reason).then((r) => r.message),
    });
  }
  return confirmAction({
    title: "Banir fantasma",
    message:
      `${ghost.name} e qualquer outro fantasma do IP ${ghost.ip} saem da cidade e não entram mais como fantasma. ` +
      "Quem tem conta nessa conexão continua entrando. Atenção: casas e redes públicas dividem o mesmo IP.",
    confirmLabel: "Banir IP",
    danger: true,
    fields: [reasonField(true)],
    run: (v) => modApi.banGhost(token, ghost.id, v.reason).then((r) => r.message),
  });
}

export function revokeGhostBanAction(ban: GhostBan, token: string): Promise<string | null> {
  return confirmAction({
    title: "Remover banimento de fantasma",
    message: `O IP ${ban.ip}${ban.ghostName ? ` (${ban.ghostName})` : ""} volta a poder entrar como fantasma.`,
    confirmLabel: "Remover banimento",
    run: () => modApi.revokeGhostBan(token, ban.id).then((r) => r.message),
  });
}
